import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLaunch } from '../contexts/LaunchContext';
import { StoreBadges } from './StoreBadges';
import { PhoneFrame } from './PhoneFrame';
import { screens } from '../data/screens';

/** Playful reminders of what actually goes in a kitty. */
const floatingExpenses = [{
  emoji: '🍺',
  label: 'Drinks',
  amount: '£46.74',
  position: 'left-[-6%] top-[16%]'
}, {
  emoji: '🚕',
  label: 'Taxi',
  amount: '£28.50',
  position: 'left-[-2%] bottom-[16%]'
}, {
  emoji: '🏠',
  label: 'Accommodation',
  amount: '£584.65',
  position: 'right-[-4%] top-[9%]'
}, {
  emoji: '🎟️',
  label: 'Tickets',
  amount: '£125.74',
  position: 'right-[-6%] bottom-[22%]'
}];
export function HeroSection() {
  const {
    isPublicLaunch,
    googlePlayUrl
  } = useLaunch();
  const reduceMotion = useReducedMotion();
  return <section className="relative overflow-hidden pt-32 pb-16 sm:pt-36 lg:pt-40 lg:pb-24" data-id="element-155" data-mp="3k9b88180d56e">
      {/* Warm wash behind the composition */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" data-id="element-156" data-mp="2fgjzgxjupmon">
        <div className="absolute -right-[10%] -top-[15%] h-[65%] w-[60%] rounded-full bg-kitty-yellow-100/50 blur-3xl" data-id="element-157" data-mp="2begixuibrrwi" />
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-8" data-id="element-158" data-mp="2p6wi2k83v5s5">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-8" data-id="element-159" data-mp="2kev5syaxtf24">
          {/* Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-5" data-id="element-160" data-mp="2z2zuciwo8kst">
            {isPublicLaunch && <p className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-bold text-kitty-dark shadow-sm" data-id="element-161" data-mp="381tlhtr8giyz">
                <span className="relative flex h-2.5 w-2.5" data-id="element-162" data-mp="1dqokyybg0mnv">
                  <span aria-hidden="true" className="absolute inline-flex h-full w-full animate-ping rounded-full bg-kitty-green opacity-75" data-id="element-163" data-mp="18rlk7q2hx2lp" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-kitty-green" data-id="element-164" data-mp="38v5tb07xlopf" />
                </span>
                Out now
              </p>}

            <h1 className="mb-6 text-[2.5rem] font-black leading-[1.05] tracking-tight text-kitty-dark sm:text-5xl xl:text-[3.75rem]" data-id="element-165" data-mp="3ir956xsl51pk">
              Split the cost.
              <br data-id="element-166" data-mp="3rs708ql1pyw0" />
              Not the friendship.
            </h1>

            <p className="mb-8 max-w-lg text-lg leading-relaxed text-gray-600 lg:text-xl" data-id="element-167" data-mp="3rrxcl9nh43th">
              One place for every group expense, so nobody has to remember who paid, chase their mates or argue over the maths.
            </p>

            {isPublicLaunch && googlePlayUrl && <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-kitty-yellow px-8 text-lg font-bold text-kitty-dark shadow-md transition-colors duration-150 hover:bg-kitty-yellow-500 hover:shadow-lg active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-dark focus-visible:ring-offset-2" data-id="element-168" data-mp="aanwzj7s2cni">
                Start a kitty, it's free
              </a>}

            <p className="mt-4 text-sm font-semibold text-gray-500" data-id="element-169" data-mp="1usycn1u2dkkg">
              Unlimited kitties • No subscription • Friends can join without the
              app
            </p>

            <div className="mt-7" data-id="element-170" data-mp="2mknzrdeakwum">
              <StoreBadges tone="light" data-id="element-171" data-mp="2mjmdecrtsnxl" />
            </div>
          </motion.div>

          {/* Product composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-7" data-id="element-172" data-mp="2ik42m9tpxt8h">
            <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[620px]" data-id="element-173" data-mp="36ag87e1hrdvw">
              {/* Behind, left: My Balance */}
              <PhoneFrame screen={screens.myBalance} decorative className="absolute left-0 top-[10%] hidden w-[42%] -rotate-[7deg] sm:block" data-id="element-174" data-mp="31gbyin0ucrr4" />

              {/* Behind, right: settlement history */}
              <PhoneFrame screen={screens.history} decorative className="absolute right-0 top-[16%] hidden w-[42%] rotate-[7deg] sm:block" data-id="element-175" data-mp="2pze63m47l3zm" />

              {/* Front and centre: the kitty itself. Above the fold, so it is
                  deliberately not lazy-loaded. */}
              <div className="relative z-20 mx-auto w-full sm:w-[52%]" data-id="element-176" data-mp="34rgbflp1sp71">
                <PhoneFrame screen={screens.kittyMain} shadow="xl" eager data-id="element-177" data-mp="38r6ypffd8n6t" />
              </div>

              {/* Floating expenses */}
              {floatingExpenses.map((expense, index) => <motion.div key={expense.label} aria-hidden="true" initial={{
              opacity: 0,
              y: 10
            }} animate={reduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 1,
              y: [0, -7, 0]
            }} transition={reduceMotion ? {
              duration: 0.3
            } : {
              opacity: {
                duration: 0.4,
                delay: 0.4 + index * 0.1
              },
              y: {
                duration: 4 + index * 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.3
              }
            }} className={`absolute z-30 hidden items-center gap-2.5 rounded-2xl border border-gray-100 bg-white px-3.5 py-2.5 shadow-card lg:flex ${expense.position}`} data-id="element-178" data-mp="2zzeq7a9wjo4v" data-mpi={index}>
                  <span className="text-lg leading-none" data-id="element-179" data-mp="3ew64f22h4iz4">{expense.emoji}</span>
                  <span className="text-sm font-bold text-kitty-dark" data-id="element-180" data-mp="3sve6q3txo77j">
                    {expense.label}
                  </span>
                  <span className="text-sm font-black text-kitty-dark" data-id="element-181" data-mp="3ntymuf3hs6qq">
                    {expense.amount}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}